// ═══════════════════════════════════════════════════════════════
// BOUNTIFUL SEA - TYPE DEFINITIONS
// A push-your-luck dice gambling game with a nautical theme
// ═══════════════════════════════════════════════════════════════

// ─────────────────────────────────────────────────────────────────
// CORE ENUMS & TYPES
// ─────────────────────────────────────────────────────────────────

export type GamePhase =
  | 'setup'           // Configure players and rules
  | 'roundStart'      // Beginning of a new round
  | 'playerTransition' // "Pass device to [Player]" privacy screen
  | 'voyage'          // Active player's turn
  | 'roundEnd'        // Summary - who lost dice
  | 'victory';        // Game over

export type VoyageSubPhase =
  | 'rolling'         // Dice being rolled / player sees their dice
  | 'declaring'       // Player inputs their declared total
  | 'awaitingCalls'   // Other players can call Scoundrel
  | 'callResolution'  // Revealing truth if called
  | 'decision';       // Sail On or Make Port choice

export type AIPersonality = 'cautious' | 'balanced' | 'reckless';

export type PlayerId = 1 | 2 | 3 | 4 | 5 | 6;

export type ShipwreckRule = 'depthsClaimAll' | 'washAshore';

// ─────────────────────────────────────────────────────────────────
// PLAYER TYPES
// ─────────────────────────────────────────────────────────────────

export interface VoyageState {
  tideDieValue: number;           // Public die result (1-6)
  scoundrelDiceValues: number[];  // Secret dice results
  actualTotal: number;            // True sum of all dice
  declaredTotal: number | null;   // What player claims (can be a lie)
  haul: number;                   // Coins claimed this voyage
  previousDeclarations: number[]; // Must declare higher each roll
  rollCount: number;              // Number of times rolled this voyage
}

export interface Player {
  id: PlayerId;
  name: string;
  color: string;
  isAI: boolean;
  aiPersonality: AIPersonality | null;

  stash: number;           // Secured coins (safe, permanent)
  scoundrelDice: number;   // Number of secret dice (lose when caught/lowest)
  isEliminated: boolean;   // Out when scoundrelDice === 0

  // Current voyage state (null when not on a voyage)
  currentVoyage: VoyageState | null;
}

export interface PlayerSetup {
  name: string;
  isAI: boolean;
  aiPersonality?: AIPersonality;
  color?: string;
}

// ─────────────────────────────────────────────────────────────────
// GAME CONFIGURATION
// ─────────────────────────────────────────────────────────────────

export interface GameConfig {
  playerCount: number;           // 2-6
  startingDice: number;          // 1-4 scoundrel dice per player
  anteAmount: number;            // Coins each player contributes to starting pot
  shipwreckRule: ShipwreckRule;  // What happens when eliminated
}

// ─────────────────────────────────────────────────────────────────
// ROUND & TURN STATE
// ─────────────────────────────────────────────────────────────────

export interface RoundState {
  roundNumber: number;
  turnOrder: PlayerId[];           // Rotates each round
  currentTurnIndex: number;        // Index in turnOrder
  currentPlayerId: PlayerId;

  // Track hauls this round for comparison at round end
  playerHauls: Partial<Record<PlayerId, number>>;
  playersWhoMadePort: PlayerId[];
  playersWhoSank: PlayerId[];
}

export interface CallState {
  callerId: PlayerId;
  targetId: PlayerId;
  wasLying: boolean | null;  // null until resolved
  resolved: boolean;
}

export interface CallWindow {
  isOpen: boolean;
  callers: PlayerId[];      // Players who can still call
  passedPlayers: PlayerId[]; // Players who passed on calling
}

// ─────────────────────────────────────────────────────────────────
// ROUND HISTORY
// ─────────────────────────────────────────────────────────────────

export interface RoundSummary {
  roundNumber: number;
  hauls: Partial<Record<PlayerId, number>>;
  loserPlayerIds: PlayerId[];  // Can be multiple if tied
  diceRemoved: number;
  potRemaining: number;
}

// ─────────────────────────────────────────────────────────────────
// GAME STATE
// ─────────────────────────────────────────────────────────────────

export interface GameState {
  phase: GamePhase;
  voyageSubPhase: VoyageSubPhase | null;
  config: GameConfig;

  bountifulSea: number;           // The shared pot of coins
  players: Partial<Record<PlayerId, Player>>;
  playerOrder: PlayerId[];        // All players in game order
  round: RoundState;

  // Call tracking
  pendingCall: CallState | null;
  callWindow: CallWindow;

  // UI State
  showDice: boolean;              // Toggle for hot-seat privacy
  animatingDice: boolean;         // Dice roll animation in progress
  message: string | null;         // Game message to display

  // History
  roundHistory: RoundSummary[];

  // Result
  winner: PlayerId | null;
  finalStandings: PlayerId[] | null; // Ordered by final stash
}

// ─────────────────────────────────────────────────────────────────
// GAME ACTIONS
// ─────────────────────────────────────────────────────────────────

export type GameAction =
  // Setup
  | { type: 'START_GAME'; config: GameConfig; players: PlayerSetup[] }
  | { type: 'RESET_GAME' }

  // Round flow
  | { type: 'BEGIN_ROUND' }
  | { type: 'CONFIRM_PLAYER_READY' }  // Player confirms they're at device

  // Voyage actions
  | { type: 'ROLL_DICE' }
  | { type: 'SET_DICE_ANIMATION_COMPLETE' }
  | { type: 'DECLARE_TOTAL'; amount: number }
  | { type: 'CALL_SCOUNDREL'; callerId: PlayerId }
  | { type: 'PASS_CALL'; playerId: PlayerId }
  | { type: 'RESOLVE_CALL' }
  | { type: 'SAIL_ON' }
  | { type: 'MAKE_PORT' }
  | { type: 'SINK' }                  // Player chooses to sink (can't/won't declare higher)

  // Round end
  | { type: 'PROCESS_ROUND_END' }
  | { type: 'CONFIRM_ROUND_SUMMARY' }

  // AI
  | { type: 'AI_TAKE_ACTION' }

  // UI
  | { type: 'SET_MESSAGE'; message: string | null }
  | { type: 'HIDE_DICE' };           // Hide dice when passing device

// ─────────────────────────────────────────────────────────────────
// UTILITY TYPES
// ─────────────────────────────────────────────────────────────────

export interface DiceRoll {
  tideDie: number;
  scoundrelDice: number[];
  total: number;
}

export interface AIDecision {
  action: 'declare' | 'sailOn' | 'makePort' | 'callScoundrel' | 'pass' | 'sink';
  value?: number;      // For declarations
  confidence: number;  // 0-1 how confident AI is
  reasoning?: string;  // For debugging/display
}

export interface AIConfig {
  lieThreshold: number;      // Probability to lie (0-1)
  bluffMultiplier: number;   // When lying, declare actual * this
  portThreshold: number;     // Port when haul/pot ratio exceeds this
  callThreshold: number;     // Call when suspicion exceeds this
  riskTolerance: number;     // General risk tolerance (0-1)
}

// ─────────────────────────────────────────────────────────────────
// GAME ACTIONS INTERFACE (for hook)
// ─────────────────────────────────────────────────────────────────

export interface GameActions {
  // Setup
  startGame: (config: GameConfig, players: PlayerSetup[]) => void;
  resetGame: () => void;

  // Round flow
  beginRound: () => void;
  confirmPlayerReady: () => void;

  // Voyage actions
  rollDice: () => void;
  declareTotal: (amount: number) => void;
  callScoundrel: (callerId: PlayerId) => void;
  passCall: (playerId: PlayerId) => void;
  resolveCall: () => void;
  sailOn: () => void;
  makePort: () => void;
  sink: () => void;

  // Round end
  processRoundEnd: () => void;
  confirmRoundSummary: () => void;

  // AI
  triggerAIAction: () => void;

  // UI
  setMessage: (message: string | null) => void;
  hideDice: () => void;
}

// ─────────────────────────────────────────────────────────────────
// GAME HELPERS INTERFACE (for hook)
// ─────────────────────────────────────────────────────────────────

export interface GameHelpers {
  // Player queries
  getCurrentPlayer: () => Player | undefined;
  getPlayer: (id: PlayerId) => Player | undefined;
  getActivePlayers: () => Player[];
  getNonEliminatedPlayers: () => Player[];
  isPlayerEliminated: (playerId: PlayerId) => boolean;

  // Voyage queries
  getMinDeclaration: () => number;
  getMaxPossibleRoll: () => number;
  canDeclare: (amount: number) => boolean;
  canSailOn: () => boolean;
  mustSinkOrLie: () => boolean;

  // Call queries
  canCallScoundrel: (playerId: PlayerId) => boolean;
  getCallablePlayers: () => Player[];

  // Haul calculations
  calculateHaulFromDeclaration: (declaration: number) => number;
  getPotentialHaul: () => number;

  // AI
  isCurrentPlayerAI: () => boolean;
  getAIDecision: () => AIDecision | null;
}

// ─────────────────────────────────────────────────────────────────
// PLAYER COLORS
// ─────────────────────────────────────────────────────────────────

export const PLAYER_COLORS: string[] = [
  '#D4AF37', // Gold
  '#2A7B7B', // Teal
  '#8B4513', // Saddle Brown
  '#6B238E', // Purple
  '#CD5C5C', // Indian Red
  '#2F4F4F', // Dark Slate Gray
];
